import { useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';

import { TARGET_VALUE, type ChaseBarConfig } from './config';

export type ChaseStatus = 'hidden' | 'active' | 'completed' | 'ended';

export interface ChaseProgressState {
  status: ChaseStatus;
  progress: number;
  currentValue: number;
  timeRemaining: number;
}

interface Waypoint {
  timeMs: number;
  value: number;
}

interface CachedTimestamps {
  startMs: number;
  completedMs: number;
  endMs: number;
  waypoints: Waypoint[];
}

const HIDDEN_STATE: ChaseProgressState = {
  status: 'hidden',
  progress: 0,
  currentValue: 0,
  timeRemaining: 0,
};

// Wobble: integral of two sines whose frequencies are integer multiples of 2π,
// so the deviation is zero at t=0 and t=1 — waypoints are hit exactly. Combined
// slope stays in (1 - 0.023, 1 + 0.023), so the curve is monotonically increasing.
const FREQ_1 = 2 * Math.PI * 3;
const FREQ_2 = 2 * Math.PI * 7;

function easedT(t: number): number {
  const wobble = (0.015 * (1 - Math.cos(FREQ_1 * t))) / FREQ_1 + (0.008 * (1 - Math.cos(FREQ_2 * t))) / FREQ_2;
  return t + wobble;
}

function interpolateValue(waypoints: Waypoint[], now: number): number {
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i];
    const b = waypoints[i + 1];
    if (now <= b.timeMs) {
      const span = b.timeMs - a.timeMs;
      const t = span > 0 ? (now - a.timeMs) / span : 1;
      return a.value + easedT(t) * (b.value - a.value);
    }
  }
  return waypoints[waypoints.length - 1].value;
}

/**
 * Compute a monotonically increasing progress value from time.
 *
 * The bar fills piecewise between waypoints (start → each timed milestone →
 * completedDate), so each milestone is reached at its configured `time`. Within
 * each segment the pace varies organically (sine-integral speed variation) but
 * always hits waypoints exactly. Milestones without a `time` are ignored for
 * interpolation (they still render as markers).
 *
 * After completedDate the bar stays at 100% until endDate, then hides.
 */
export function computeChaseProgress(
  targetValue: number,
  timestamps: CachedTimestamps,
  now: number,
): ChaseProgressState {
  const { startMs, completedMs, endMs, waypoints } = timestamps;

  if (now < startMs) {
    return HIDDEN_STATE;
  }

  if (now >= endMs) {
    return {
      status: 'ended',
      progress: 1,
      currentValue: targetValue,
      timeRemaining: 0,
    };
  }

  if (now >= completedMs) {
    return {
      status: 'completed',
      progress: 1,
      currentValue: targetValue,
      timeRemaining: endMs - now,
    };
  }

  const value = interpolateValue(waypoints, now);
  const progress = Math.min(1, value / targetValue);

  return {
    status: 'active',
    progress,
    currentValue: Math.round(value),
    timeRemaining: endMs - now,
  };
}

const TICK_INTERVAL_MS = 1000;

export function useChaseProgress(config: ChaseBarConfig): ChaseProgressState {
  const milestonesKey = config.milestones.map((m) => `${m.value}:${m.time ?? ''}`).join(',');
  const targetValue = TARGET_VALUE;

  const timestamps = useMemo<CachedTimestamps>(() => {
    const startMs = dayjs(config.startDate).valueOf();
    const completedMs = dayjs(config.completedDate).valueOf();
    const endMs = dayjs(config.endDate).valueOf();

    const waypoints: Waypoint[] = [{ timeMs: startMs, value: 0 }];
    for (const milestone of config.milestones) {
      if (!milestone.time) continue;
      const timeMs = dayjs(milestone.time).valueOf();
      if (!Number.isFinite(timeMs) || timeMs <= startMs || timeMs >= completedMs) continue;
      waypoints.push({ timeMs, value: milestone.value });
    }
    waypoints.push({ timeMs: completedMs, value: targetValue });
    waypoints.sort((a, b) => a.timeMs - b.timeMs);

    // Enforce non-decreasing values so a bad config can never cause the fill to go backward.
    let maxValue = 0;
    for (const wp of waypoints) {
      wp.value = Math.max(wp.value, maxValue);
      maxValue = wp.value;
    }

    return { startMs, completedMs, endMs, waypoints };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.startDate, config.completedDate, config.endDate, targetValue, milestonesKey]);

  const [state, setState] = useState<ChaseProgressState>(() =>
    computeChaseProgress(targetValue, timestamps, Date.now()),
  );

  useEffect(() => {
    const tick = () => {
      setState((prev) => {
        const next = computeChaseProgress(targetValue, timestamps, Date.now());
        if (next.status !== 'active') return next;
        // Clamp against prev so clock skew, config reloads, or throttled tabs can never walk the bar backward.
        return {
          ...next,
          progress: Math.max(prev.progress, next.progress),
          currentValue: Math.max(prev.currentValue, next.currentValue),
        };
      });
    };

    tick();
    const id = setInterval(tick, TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [targetValue, timestamps]);

  return state;
}
